/**
 * The web game registry — the UI half of the platform's two-integration-
 * point contract (docs/APP_PLAN.md §5.2): `gameId -> lazy React component`.
 *
 * Adding a game touches exactly two places in the whole platform: the
 * server-side game manifest (`packages/games/index.ts`) and this map.
 * Nothing else changes. This file — along with everything else under
 * `apps/web/src/games/**` — is the one documented, permitted place for a
 * registered `gameId` string literal to appear in web platform code (see
 * the architecture test excluding `games/**`, docs/APP_PLAN.md §10.3).
 */
import type { GameCatalogArt, OwnedGameProfileArt } from '../assets/asset-manifest.js';
import type { GameComponentLoader, LobbyConfigComponentLoader } from './types.js';

export const GAME_REGISTRY: Record<string, GameComponentLoader> = {
  'secret-number': () => import('./secret-number/GameScreen.js'),
  'word-rush': () => import('./word-rush/GameScreen.js'),
  // The full UI build (docs/games/word-delve/GAME_PLAN.md §20): lobby
  // through a complete run to results, all three input paths (pointer/tap/
  // keyboard), live peer paths, mobile layout, the accessibility pass, and
  // the client-side Word Power preview. `defaultStatus: 'active'`
  // (`packages/games/word-delve/src/metadata.ts`) since M11.
  'word-delve': () => import('./word-delve/GameScreen.js'),
  // Solo, timed, three-floor roguelike word-deduction run (docs/games/
  // rogue-rush/GAME_PLAN.md §11.3). `defaultStatus: 'active'`
  // (`packages/games/rogue-rush/src/metadata.ts`) since M11. No
  // `LOBBY_CONFIG_REGISTRY` entry: it declares no `joinModel` (D14), so the
  // create-lobby dialog never looks it up.
  'rogue-rush': () => import('./rogue-rush/GameScreen.js'),
};

/**
 * `gameId -> lazy lobby-settings form`, for the create-lobby dialog
 * (docs/games/word-rush/GAME_PLAN.md §11, W1). Only games declaring
 * `joinModel: 'open_lobby'` need an entry; a game with none gets a generic
 * "no extra settings" message from the dialog instead. Word Delve also
 * declares `joinModel: 'open_lobby'` (its own metadata) — its form has
 * nothing to configure (`wordDelveConfigSchema` is `z.object({}).strict()`,
 * D18), but is still registered so the dialog shows its own game-owned
 * explanation of that rather than the platform's generic fallback copy
 * (`components/LobbyConfigForm.tsx`'s own doc comment).
 */
export const LOBBY_CONFIG_REGISTRY: Record<string, LobbyConfigComponentLoader> = {
  'word-rush': () => import('./word-rush/components/LobbyConfigForm.js'),
  'word-delve': () => import('./word-delve/components/LobbyConfigForm.js'),
};

/**
 * `gameId -> game-owned catalog card artwork` (docs/UI_GUIDELINES.md §12,
 * docs/games/word-rush/GAME_PLAN.md §4). `GameCard.tsx` looks a `gameId` up
 * here and falls back to the shared `game.catalog.default` placeholder
 * (`apps/web/src/assets/asset-manifest.ts`'s `resolveGameCatalogArt`) for any
 * game with no entry.
 *
 * Every registered game has its own image: a card is meant to be
 * recognizable before its name is read, so no two games may share the generic
 * placeholder. Each entry ships the 1x/2x pair §12 asks for, derived from the
 * supplied master by `scripts/build-game-cards.mjs`.
 */
export const GAME_CATALOG_ART: Record<string, GameCatalogArt> = {
  'secret-number': {
    src: '/assets/games/secret-number/catalog-640.webp',
    srcSet:
      '/assets/games/secret-number/catalog-640.webp 640w, /assets/games/secret-number/catalog-1280.webp 1280w',
    // The catalog banner is a far wider, shallower window than the profile's,
    // so the profile anchor above does not transfer. A plain `cover` fit is
    // width-constrained here, which would show the master end to end — and its
    // left two-thirds is apothecary shelving, leaving the scrying table that
    // actually identifies the game as a detail in the corner. Zooming to 190%
    // and anchoring hard right frames the table itself: orb, candles, skull and
    // open grimoire, with enough shelf and hanging herbs still in frame to read
    // as the same laboratory. 48% vertically centres the orb in the band.
    bannerSrc: '/assets/games/secret-number/catalog-1280.webp',
    // 165% rather than the 190% first tried, and pulled back off the right
    // edge: at the harder zoom the orb was pressed against the crop boundary
    // with its table cut away, which read as a detail shot rather than a scene.
    // This frames the whole scrying setup — orb, candles, skull, open grimoire
    // and the moon-embroidered table runner — and still carries enough shelving
    // and hanging herbs behind it to read as the laboratory.
    bannerSize: '165%',
    bannerPosition: '88% 52%',
  },
  'word-rush': {
    src: '/assets/games/word-rush/catalog-640.webp',
    srcSet:
      '/assets/games/word-rush/catalog-640.webp 640w, /assets/games/word-rush/catalog-1280.webp 1280w',
    // This master already composes well for a wide band — the moonlit arch
    // sits left, the glowing letter swirl and open book right — so it needs
    // only a light 124% zoom to drop the empty shelving at the extreme edges,
    // keeping both halves of that composition. 37% vertically holds the whole
    // swirl and the book it rises from, with the desk's front edge grounding
    // the lower edge of the band.
    bannerSrc: '/assets/games/word-rush/catalog-1280.webp',
    bannerSize: '124%',
    bannerPosition: '60% 37%',
  },
  'word-delve': {
    src: '/assets/games/word-delve/catalog-640.webp',
    srcSet:
      '/assets/games/word-delve/catalog-640.webp 640w, /assets/games/word-delve/catalog-1280.webp 1280w',
    // Composed dead centre and symmetrically: the lit cross of letter tiles on
    // the flagstones, flanked by two torches and the central arch. 130% with a
    // low vertical anchor keeps the tiles and the reflected torchlight on the
    // floor — the part that reads as a board — rather than centring on the
    // empty upper vaulting.
    bannerSrc: '/assets/games/word-delve/catalog-1280.webp',
    bannerSize: '130%',
    bannerPosition: '50% 70%',
  },
  // A game-owned, named-and-dimensioned placeholder pair (docs/games/
  // rogue-rush/GAME_PLAN.md §10, R6) built by `scripts/build-rogue-rush-
  // assets.mjs` — never the shared generic placeholder. Swapped for real
  // art by a file replacement once a `catalog-master.png` master lands,
  // with no code change here.
  'rogue-rush': {
    src: '/assets/games/rogue-rush/catalog-640.webp',
    srcSet:
      '/assets/games/rogue-rush/catalog-640.webp 640w, /assets/games/rogue-rush/catalog-1280.webp 1280w',
    // The one master whose subject is *vertical*: a tower filling the full
    // height of a 16:9 frame. A shallow band can never hold all of it, so this
    // deliberately takes no zoom at all (`cover`) and anchors near the top,
    // which keeps the lit crown, the battlements and the start of the spiral
    // stair — the silhouette that identifies it — instead of slicing a band of
    // masonry out of the middle. 2% rather than 0% leaves a thin line of sunset
    // sky above the crown so it does not butt against the frame.
    bannerSrc: '/assets/games/rogue-rush/catalog-1280.webp',
    bannerPosition: 'center 2%',
  },
};

/**
 * `gameId -> a short catalog-card summary`, used in place of the game's own
 * `description` on the Games page only.
 *
 * Presentation copy, nothing more: the authoritative description still lives in
 * each game's `metadata.ts` and is what every other surface renders. A catalog
 * tile reserves a fixed two-line region so a row of cards ends at one Y
 * coordinate, and a description written to explain a game in full (Word Delve's
 * runs to four lines at card width) would otherwise either set the height of
 * the whole row or be clipped mid-sentence. A game with no entry here falls
 * back to its real description, which for the others is already about two
 * lines.
 */
export const GAME_CATALOG_SUMMARY: Record<string, string> = {
  'word-delve':
    'Descend as a party through a procedurally branching dungeon. Trace words to gain power, find relics, and defeat the boss.',
};

/**
 * `gameId -> the game's own art for its card in the profile Gameplay section`
 * (APP_SPEC §13.3): the square emblem, plus how that game's banner is
 * cropped. `GameCardHeader` looks a `gameId` up here and falls back to shared
 * defaults (`resolveGameProfileArt`) for any game with no entry. Like
 * `GAME_CATALOG_ART`, this is one of the sanctioned places a registered
 * `gameId` string literal may appear (docs/APP_PLAN.md §10.3).
 *
 * The card's header *background image* is not listed here on purpose: it is
 * the game's `GAME_CATALOG_ART` image reused, so every game is recognisable
 * by one picture and a new game needs no profile-background entry at all.
 *
 * `bannerPosition` is the one thing that genuinely varies per artwork. The
 * banner is a wide band cut from a 16:9 illustration, so only about half the
 * source height is visible, and *which* band reads best is a fact about the
 * painting — where its subject happens to sit — not a rule about the game.
 * So expect to re-tune the anchor whenever a master is replaced, and judge
 * it at both the desktop and mobile band heights: the value that frames a
 * subject well in one can clip it in the other. Both stay a plain `cover`
 * fit — the art is never zoomed in past `cover`, because the goal is to
 * show more of it, not less.
 *
 * All four emblems are now final owner-supplied art: 216² PNGs, the size the
 * card markup declares, served as-is with no build step. Replacing one is a
 * matter of dropping a same-named file into
 * `apps/web/public/assets/games/<id>/`; only a new *path* needs an edit to the
 * `icon` below.
 *
 * Note that a game's *display name* must not be written out in prose here —
 * refer to a game by its id. Each game's name has a single source (its own
 * `metadata.ts`), and a per-game test fails the build when the literal appears
 * anywhere outside that file and `docs/`.
 */
export const GAME_PROFILE_ART: Record<string, OwnedGameProfileArt> = {
  'secret-number': {
    icon: '/assets/games/secret-number/profile-icon.png',
    // Plain `cover`; only the vertical anchor is game-specific. The art is
    // composed with its subject already right of centre — the scrying orb
    // sits in the right third, well clear of where the title falls — so no
    // horizontal correction is needed. 58% is tuned to the current master:
    // the orb keeps headroom at both band heights, the candles and open
    // grimoire beside it read in full, and the crescent-moon table runner
    // grounds the lower edge instead of the band ending on bare shelving.
    bannerPosition: 'center 58%',
  },
  'word-rush': {
    icon: '/assets/games/word-rush/profile-icon.png',
    // Plain `cover`; only the vertical anchor is game-specific. 40% clears
    // the topmost letter tile so the whole swirl, its glow and the open book
    // it rises from all read, with the table's front edge grounding the
    // lower edge and the quiet moonlit arch falling under the title.
    bannerPosition: 'center 40%',
  },
  'word-delve': {
    icon: '/assets/games/word-delve/profile-icon.png',
    // Plain `cover`. Unlike the other two masters this one composes its
    // subject — the lit cross of letter tiles on the flagstones — dead
    // centre, and a `cover` banner has no horizontal slack to correct with
    // (the image is width-constrained, so only the vertical anchor moves),
    // so the tiles necessarily sit near the title's tail. 56% is the
    // compromise the two band heights force: the desktop band alone prefers
    // ~60% (the whole cross centred), the mobile band ~52% (the cross
    // dropped clear of the title), and 56% keeps all four tiles unclipped in
    // both while the staff, lantern and open chest hold the right edge.
    bannerPosition: 'center 56%',
  },
  // Final owner-supplied emblem: a gold-framed tile of the snowbound tower,
  // drawn in the same framed-square language as the other three. It replaces
  // the square crop of this game's own catalog painting that stood in for it
  // until now — which was a real square, never stretched, but read as a second
  // copy of the banner sitting on the banner rather than as a mark.
  //
  // `bannerPosition` stays at its default `'center'`, and that is about a
  // different picture: it anchors the *profile* card's banner, which is the
  // catalog painting, not this emblem. The catalog banner's own crop is
  // declared separately in `GAME_CATALOG_ART` above.
  'rogue-rush': {
    icon: '/assets/games/rogue-rush/profile-icon.png',
  },
};

/**
 * `gameId -> one-word genre tag`, shown ahead of the platform-derived
 * metadata chips ("Solo", "2 players", "Real-time" — `lib/game-meta.ts`) on
 * a catalog card. Genre is editorial rather than mechanical: nothing in
 * `GameMetadata` implies it and no platform behaviour depends on it, so it
 * stays with the other per-game display wiring here rather than becoming a
 * field every future game must fill in. A game with no entry simply shows
 * the derived chips.
 */
export const GAME_CATALOG_TAGS: Record<string, string> = {
  'secret-number': 'Deduction',
  'word-rush': 'Word game',
  'word-delve': 'Word roguelike',
  // Deliberately distinct from Word Delve's "Word roguelike" (docs/games/
  // rogue-rush/GAME_PLAN.md D24, R3): a co-op traced-path party crawl vs. a
  // solo five-letter deduction run are mechanically unrelated, but two
  // adjacent catalog cards both reading "word roguelike" would still read
  // as a product overlap.
  'rogue-rush': 'Solo roguelike',
};
